import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Chip, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, IconButton, Switch, FormControlLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Divider, MenuItem,
} from '@mui/material';
import {
  Add, Delete, Edit, Refresh, CheckCircle, NotificationsActive, Warning,
} from '@mui/icons-material';
import { validationRulesService, slackConfigService } from '../services/api';
import { SUPPORTED_METRICS } from '../config/sensorMetrics';

const OPERATORS = [
  { value: 'gt', label: 'is greater than', needsValue: true, needsValue2: false },
  { value: 'lt', label: 'is less than', needsValue: true, needsValue2: false },
  { value: 'gte', label: 'is greater than or equal to', needsValue: true, needsValue2: false },
  { value: 'lte', label: 'is less than or equal to', needsValue: true, needsValue2: false },
  { value: 'between', label: 'is between', needsValue: true, needsValue2: true },
  { value: 'outside_range', label: 'is outside range', needsValue: true, needsValue2: true },
  { value: 'is_null', label: 'is missing', needsValue: false, needsValue2: false },
];

const COOLDOWN_OPTIONS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 360, label: '6 hours' },
  { value: 1440, label: '24 hours' },
];

interface ValidationRulesTabProps {
  stationUuid: string;
  availableMetrics?: string[];
}

const ValidationRulesTab: React.FC<ValidationRulesTabProps> = ({ stationUuid, availableMetrics }) => {
  const [rules, setRules] = useState<any[]>([]);
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<any>(null);
  const [showLogs, setShowLogs] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);

  // Slack state
  const [slackWebhook, setSlackWebhook] = useState('');
  const [slackEnabled, setSlackEnabled] = useState(false);
  const [slackWebhookSet, setSlackWebhookSet] = useState(false);
  const [slackSaving, setSlackSaving] = useState(false);
  const [slackTesting, setSlackTesting] = useState(false);

  const [form, setForm] = useState({
    metric: '', operator: 'gt', value: '', value2: '',
    action: 'reject', message: '', enabled: true,
    alert_cooldown_minutes: 60, notify_slack: false,
  });

  const metricOptions = SUPPORTED_METRICS
    .filter(m => availableMetrics && availableMetrics.includes(m.internal))
    .map(m => ({
      value: m.internal,
      label: `${m.userFriendly.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')} (${m.unit})`,
    }));

  useEffect(() => { loadRules(); loadSlackConfig(); }, [stationUuid]);

  const loadRules = async () => {
    setLoading(true);
    try {
      const data = await validationRulesService.list(stationUuid);
      setRules(data);
    } catch {
      setError('Failed to load rules');
    } finally {
      setLoading(false);
    }
  };

  const loadSlackConfig = async () => {
    try {
      const data = await slackConfigService.get(stationUuid);
      setSlackEnabled(data.slack_enabled);
      setSlackWebhookSet(data.webhook_set);
    } catch { /* ignore */ }
  };

  const handleSlackSave = async () => {
    setSlackSaving(true); setError('');
    try {
      await slackConfigService.save(stationUuid, slackWebhook);
      setSlackWebhookSet(true); setSlackEnabled(true); setSlackWebhook('');
      setSuccess('Slack webhook saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save webhook');
    } finally { setSlackSaving(false); }
  };

  const handleSlackTest = async () => {
    setSlackTesting(true); setError('');
    try {
      await slackConfigService.test(stationUuid);
      setSuccess('Test message sent to Slack');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send test');
    } finally { setSlackTesting(false); }
  };

  const handleSlackRemove = async () => {
    if (!window.confirm('Remove Slack webhook?')) return;
    try {
      await slackConfigService.delete(stationUuid);
      setSlackWebhookSet(false); setSlackEnabled(false);
      setSuccess('Slack webhook removed');
      setTimeout(() => setSuccess(''), 3000);
    } catch { setError('Failed to remove webhook'); }
  };

  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const data = await validationRulesService.log(stationUuid);
      setLogs(data);
      setShowLogs(true);
    } catch {
      setError('Failed to load violation log');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingRule(null);
    setForm({ metric: '', operator: 'gt', value: '', value2: '', action: 'reject', message: '', enabled: true, alert_cooldown_minutes: 60, notify_slack: false });
    setDialogOpen(true);
  };

  const handleOpenEdit = (rule: any) => {
    setEditingRule(rule);
    setForm({
      metric: rule.metric, operator: rule.operator,
      value: rule.value !== null ? String(rule.value) : '',
      value2: rule.value2 !== null ? String(rule.value2) : '',
      action: rule.action, message: rule.message, enabled: rule.enabled,
      alert_cooldown_minutes: rule.alert_cooldown_minutes || 60,
      notify_slack: rule.notify_slack || false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setError('');
    const op = OPERATORS.find(o => o.value === form.operator);
    const payload: any = {
      metric: form.metric, operator: form.operator, action: form.action,
      message: form.message, enabled: form.enabled,
      value: op?.needsValue ? parseFloat(form.value) : null,
      value2: op?.needsValue2 ? parseFloat(form.value2) : null,
      alert_cooldown_minutes: form.action === 'alert' ? form.alert_cooldown_minutes : 60,
      notify_slack: form.action === 'alert' ? form.notify_slack : false,
    };
    try {
      if (editingRule) {
        await validationRulesService.update(stationUuid, editingRule.id, payload);
        setSuccess('Rule updated');
      } else {
        await validationRulesService.create(stationUuid, payload);
        setSuccess('Rule created');
      }
      setDialogOpen(false);
      loadRules();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save rule');
    }
  };

  const handleDelete = async (ruleId: number) => {
    if (!window.confirm('Delete this rule?')) return;
    try {
      await validationRulesService.delete(stationUuid, ruleId);
      loadRules();
      setSuccess('Rule deleted');
      setTimeout(() => setSuccess(''), 3000);
    } catch { setError('Failed to delete rule'); }
  };

  const handleToggle = async (rule: any) => {
    try {
      await validationRulesService.update(stationUuid, rule.id, { enabled: !rule.enabled });
      loadRules();
    } catch { setError('Failed to toggle rule'); }
  };

  const handleAcknowledge = async (rule: any) => {
    try {
      await validationRulesService.acknowledge(stationUuid, rule.id);
      loadRules();
      setSuccess('Alert acknowledged');
      setTimeout(() => setSuccess(''), 3000);
    } catch { setError('Failed to acknowledge'); }
  };

  const getMetricLabel = (metric: string) => {
    const m = SUPPORTED_METRICS.find(s => s.internal === metric);
    if (!m) return metric;
    return m.userFriendly.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const getOperatorLabel = (op: string) => OPERATORS.find(o => o.value === op)?.label || op;

  const formatRuleCondition = (rule: any) => {
    const metric = getMetricLabel(rule.metric);
    const op = getOperatorLabel(rule.operator);
    if (rule.operator === 'is_null') return `${metric} ${op}`;
    if (rule.operator === 'between' || rule.operator === 'outside_range')
      return `${metric} ${op} ${rule.value} — ${rule.value2}`;
    return `${metric} ${op} ${rule.value}`;
  };

  const selectedOp = OPERATORS.find(o => o.value === form.operator);
  const triggeredAlerts = rules.filter(r => r.action === 'alert' && r.is_triggered);

  if (loading) {
    return <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>;
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Triggered Alerts Banner */}
      {triggeredAlerts.length > 0 && (
        <Card sx={{ mb: 2, border: '1px solid', borderColor: 'warning.main' }}>
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Warning color="warning" sx={{ fontSize: 20 }} />
              <Typography variant="subtitle2" color="warning.main" sx={{ fontWeight: 700 }}>
                Active Alerts ({triggeredAlerts.length})
              </Typography>
            </Box>
            {triggeredAlerts.map(rule => (
              <Box key={rule.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                <NotificationsActive color="warning" sx={{ fontSize: 16 }} />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {formatRuleCondition(rule)} {rule.message && `— ${rule.message}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {rule.last_triggered_at ? new Date(rule.last_triggered_at).toLocaleString('en-GB') : ''}
                </Typography>
                <Button size="small" variant="outlined" startIcon={<CheckCircle />}
                  onClick={() => handleAcknowledge(rule)} sx={{ fontSize: '0.7rem' }}>
                  Acknowledge
                </Button>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Rules ({rules.length})
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" startIcon={<Refresh />} onClick={loadLogs} disabled={logsLoading}
            sx={{ color: 'rgba(255,255,255,0.6)' }}>
            {showLogs ? 'Refresh Log' : 'Violation Log'}
          </Button>
          <Button size="small" variant="contained" startIcon={<Add />} onClick={handleOpenCreate}
            disabled={metricOptions.length === 0}
            sx={{ background: 'linear-gradient(135deg, #00d4ff, #0099cc)' }}>
            Add Rule
          </Button>
        </Box>
      </Box>

      {/* Slack Config Section — only show if there are alert rules */}
      {rules.some(r => r.action === 'alert') && (
        <Card sx={{ mb: 2, background: 'rgba(0,0,0,0.1)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>
              Slack Notifications (for Alert rules)
            </Typography>
            {slackWebhookSet ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label="Connected" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                <Button size="small" onClick={handleSlackTest} disabled={slackTesting}
                  sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)' }}>
                  {slackTesting ? 'Sending...' : 'Send Test'}
                </Button>
                <Button size="small" onClick={handleSlackRemove}
                  sx={{ fontSize: '0.75rem', color: '#f44336' }}>
                  Remove
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField size="small" placeholder="https://hooks.slack.com/services/..." value={slackWebhook}
                  onChange={e => setSlackWebhook(e.target.value)}
                  sx={{ flex: 1 }} inputProps={{ style: { fontSize: '0.85rem' } }} />
                <Button size="small" variant="contained" onClick={handleSlackSave}
                  disabled={slackSaving || !slackWebhook.match(/^https:\/\/hooks\.slack\.com\/services\/.+/)}>
                  {slackSaving ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      <Alert severity="info" sx={{ mb: 2, fontSize: '0.85rem' }}>
        <strong>Reject</strong> nullifies the value. <strong>Flag</strong> keeps value but logs violation.
        <strong> Alert</strong> triggers a dashboard alert with cooldown anti-spam.
      </Alert>

      {/* Rules list */}
      {rules.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {metricOptions.length === 0
              ? 'This station has no measurements yet. Rules can be created once the station starts sending data.'
              : 'No rules configured. Click "Add Rule" to create one.'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
          {rules.map((rule) => (
            <Card key={rule.id} sx={{
              background: 'rgba(0,0,0,0.15)',
              border: `1px solid ${rule.is_triggered ? '#ff9800' : rule.enabled ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.03)'}`,
              opacity: rule.enabled ? 1 : 0.5,
            }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Switch size="small" checked={rule.enabled} onChange={() => handleToggle(rule)} color="success" />
                {rule.action === 'alert' && rule.is_triggered && (
                  <NotificationsActive color="warning" sx={{ fontSize: 18 }} />
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    IF {formatRuleCondition(rule)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {rule.message}
                    {rule.action === 'alert' && ` (cooldown: ${COOLDOWN_OPTIONS.find(c => c.value === rule.alert_cooldown_minutes)?.label || rule.alert_cooldown_minutes + 'min'})`}
                  </Typography>
                </Box>
                <Chip
                  label={rule.action === 'reject' ? 'Reject' : rule.action === 'flag' ? 'Flag' : 'Alert'}
                  size="small"
                  color={rule.action === 'reject' ? 'error' : rule.action === 'alert' ? 'error' : 'warning'}
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
                <IconButton size="small" onClick={() => handleOpenEdit(rule)}
                  sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleDelete(rule.id)}
                  sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#f44336' } }}>
                  <Delete fontSize="small" />
                </IconButton>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Violation Log */}
      {showLogs && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Recent Violations (last 50)
          </Typography>
          {logs.length === 0 ? (
            <Alert severity="info">No violations recorded.</Alert>
          ) : (
            <TableContainer sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Metric</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell>Violation</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{new Date(log.timestamp).toLocaleString('en-GB')}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{getMetricLabel(log.measure)}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{log.value ?? '—'}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        <Chip label={log.violation} size="small"
                          color={log.violation?.startsWith('Reject') ? 'error' : log.violation?.startsWith('Alert') ? 'error' : 'warning'}
                          variant="outlined" sx={{ fontSize: '0.7rem' }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth
        sx={{ zIndex: 10002 }}>
        <DialogTitle>{editingRule ? 'Edit Rule' : 'Add Rule'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Metric *" value={form.metric}
                onChange={e => setForm({ ...form, metric: e.target.value })}
                SelectProps={{ MenuProps: { style: { zIndex: 10003 } } }}>
                {metricOptions.map(m => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Condition *" value={form.operator}
                onChange={e => setForm({ ...form, operator: e.target.value })}
                SelectProps={{ MenuProps: { style: { zIndex: 10003 } } }}>
                {OPERATORS.map(o => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            {selectedOp?.needsValue && (
              <Grid item xs={selectedOp.needsValue2 ? 6 : 12}>
                <TextField fullWidth size="small" label={selectedOp.needsValue2 ? 'Min value *' : 'Value *'}
                  type="number" value={form.value}
                  onChange={e => setForm({ ...form, value: e.target.value })} />
              </Grid>
            )}
            {selectedOp?.needsValue2 && (
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Max value *" type="number" value={form.value2}
                  onChange={e => setForm({ ...form, value2: e.target.value })} />
              </Grid>
            )}
            <Grid item xs={form.action === 'alert' ? 6 : 12}>
              <TextField select fullWidth size="small" label="Action *" value={form.action}
                onChange={e => setForm({ ...form, action: e.target.value })}
                SelectProps={{ MenuProps: { style: { zIndex: 10003 } } }}>
                <MenuItem value="reject">Reject (nullify value)</MenuItem>
                <MenuItem value="flag">Flag (keep value, log warning)</MenuItem>
                <MenuItem value="alert">Alert (dashboard notification)</MenuItem>
              </TextField>
            </Grid>
            {form.action === 'alert' && (
              <Grid item xs={6}>
                <TextField select fullWidth size="small" label="Cooldown" value={form.alert_cooldown_minutes}
                  onChange={e => setForm({ ...form, alert_cooldown_minutes: parseInt(e.target.value) })}
                  SelectProps={{ MenuProps: { style: { zIndex: 10003 } } }}>
                  {COOLDOWN_OPTIONS.map(c => (
                    <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            {form.action === 'alert' && slackWebhookSet && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={form.notify_slack} onChange={e => setForm({ ...form, notify_slack: e.target.checked })} color="info" />}
                  label="Also notify via Slack"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Message (optional)" value={form.message}
                onChange={e => setForm({ ...form, message: e.target.value })}
                placeholder="e.g., Extreme temperature - possible sensor error" />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} color="success" />}
                label="Enabled"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.metric}>
            {editingRule ? 'Save Changes' : 'Create Rule'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ValidationRulesTab;
